// BI migration use cases, one per legacy platform. Same entry format as
// the wls-site use cases: each becomes a page at /<id>, a link in the side
// menu, and (here) a card in the home page "Migrations we deliver" grid
// (see Home.jsx -- the generic "Explore use cases" grid is off via
// site.showUseCaseGrid). Each page body comes from its file in
// platformPages/ (rendered by PlatformMigrationContent via
// `components.richContent`), and `hero` adds the page hero copy.
//
// Extra fields used only by the home page platform cards:
//   abbr    -- the two-letter badge on the card
//   name    -- platform name for the card link ("Explore Domo migrations")
//   bullets -- the card's bullet list
//
// When a walkthrough video exists, add `video: { url }` to components (it
// replaces the hero card), and optionally `text: { heading, body }`.
import { domoPage } from './platformPages/domo';
import { qlikPage } from './platformPages/qlik';
import { powerBiPage } from './platformPages/power-bi';
import { tableauPage } from './platformPages/tableau';
import { sisensePage } from './platformPages/sisense';
import { lookerPage } from './platformPages/looker';
import { strategyPage } from './platformPages/strategy';
import { ssrsPage } from './platformPages/ssrs';
import { ibmCognosPage } from './platformPages/ibm-cognos';
import { spotfirePage } from './platformPages/spotfire';

export const useCases = [
  {
    id: 'domo',
    title: 'Domo to Amazon Quick',
    route: '/domo',
    description: 'Move Domo cards, pages, and DataFlows to Quick Sight and AWS-native pipelines.',
    category: 'BI Migration',
    icon: 'dashboard',
    about: 'Moves Domo content, data, and security to Amazon Quick and AWS, with AI-led testing and wave-by-wave go-lives.',
    builtWith: ['Amazon Quick Sight', 'AWS Glue', 'Amazon Redshift', 'Amazon S3'],

    abbr: 'Do',
    name: 'Domo',
    bullets: ['Cards and pages rebuilt as Quick Sight dashboards', 'DataFlows and Magic ETL moved to AWS Glue or Redshift', 'Connector data landed in Amazon S3 or Redshift', 'PDP policies mapped to row-level security'],

    heroBanner: true,

    // Page hero copy (overrides `description`, which stays short for the
    // home page card). See UseCaseView.
    hero: {
      description: 'We move your Domo cards, DataFlows, and connectors onto Amazon Quick and AWS, then put AI agents on top of the same data.',
      tagline: 'De-risk the move with a partner who brings hands-on migration expertise and AWS funding to the table.',
      primary: { label: 'See how we use your metadata', target: 'metadata' },
      showContact: true,
      badges: [
        { icon: 'shield', label: 'AWS-funded proofs of concept' },
        { icon: 'bolt', label: 'Built on Amazon Quick' },
      ],
    },

    components: {
      heroCard: {
        title: "Let's get started",
        description: 'We have access to AWS funding for migrations. Start with a funded assessment, then see your first dashboards live on Amazon Quick in 45 days.',
        buttonLabel: 'Start my migration',
        url: '/contact',
        icon: 'clock',
      },
      // Page body, from the Domo sub-page design (see PlatformMigrationContent).
      richContent: 'PlatformMigrationContent',
      platformPage: domoPage,
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'qlik',
    title: 'Qlik to Amazon Quick',
    route: '/qlik',
    description: 'Migrate QlikView and Qlik Sense apps with programmatic extraction through the Qlik Engine API.',
    category: 'BI Migration',
    icon: 'dashboard',
    about: 'Moves Qlik content, data, and security to Amazon Quick and AWS, with AI-led testing and wave-by-wave go-lives.',
    builtWith: ['Amazon Quick Sight', 'AWS Glue'],

    abbr: 'Ql',
    name: 'Qlik',
    bullets: ['Sheets and visuals rebuilt as Quick Sight dashboards', 'Load scripts converted to SQL and AWS Glue jobs', 'Set analysis translated to calculated fields', 'Section access mapped to row-level security'],

    heroBanner: true,

    hero: {
      description: 'We move your QlikView and Qlik Sense apps, load scripts, and QVDs onto Amazon Quick and AWS, then put AI agents on top of the same data.',
      tagline: 'De-risk the move with a partner who brings hands-on migration expertise and AWS funding to the table.',
      primary: { label: 'See how we use your metadata', target: 'metadata' },
      showContact: true,
      badges: [
        { icon: 'shield', label: 'AWS-funded proofs of concept' },
        { icon: 'bolt', label: 'Built on Amazon Quick' },
      ],
    },

    components: {
      heroCard: {
        title: "Let's get started",
        description: 'We have access to AWS funding for migrations. Start with a funded assessment, then see your first dashboards live on Amazon Quick in 45 days.',
        buttonLabel: 'Start my migration',
        url: '/contact',
        icon: 'clock',
      },
      richContent: 'PlatformMigrationContent',
      platformPage: qlikPage,
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'power-bi',
    title: 'Power BI to Amazon Quick',
    route: '/power-bi',
    description: 'Move Power BI reports and datasets to Quick and get off per-user licensing.',
    category: 'BI Migration',
    icon: 'dashboard',
    about: 'Moves Power BI content, data, and security to Amazon Quick and AWS, with AI-led testing and wave-by-wave go-lives.',
    builtWith: ['Amazon Quick Sight', 'AWS Glue', 'Amazon Redshift'],

    abbr: 'PB',
    name: 'Power BI',
    bullets: ['Reports and visuals rebuilt as Quick Sight dashboards', 'DAX measures converted to calculated fields', 'Power Query steps moved to AWS Glue or Redshift', 'RLS roles and workspaces mapped to Quick permissions'],

    heroBanner: true,

    hero: {
      description: 'We move your Power BI reports, semantic models, and dataflows onto Amazon Quick and AWS, then put AI agents on top of the same data.',
      tagline: 'De-risk the move with a partner who brings hands-on migration expertise and AWS funding to the table.',
      primary: { label: 'See how we use your metadata', target: 'metadata' },
      showContact: true,
      badges: [
        { icon: 'shield', label: 'AWS-funded proofs of concept' },
        { icon: 'bolt', label: 'Built on Amazon Quick' },
      ],
    },

    components: {
      heroCard: {
        title: "Let's get started",
        description: 'We have access to AWS funding for migrations. Start with a funded assessment, then see your first dashboards live on Amazon Quick in 45 days.',
        buttonLabel: 'Start my migration',
        url: '/contact',
        icon: 'clock',
      },
      richContent: 'PlatformMigrationContent',
      platformPage: powerBiPage,
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'tableau',
    title: 'Tableau to Amazon Quick',
    route: '/tableau',
    description: 'Move Tableau workbooks and extracts to Quick and get off per-user licensing.',
    category: 'BI Migration',
    icon: 'dashboard',
    about: 'Moves Tableau content, data, and security to Amazon Quick and AWS, with AI-led testing and wave-by-wave go-lives.',
    builtWith: ['Amazon Quick Sight', 'AWS Glue', 'Amazon Redshift'],

    abbr: 'Tb',
    name: 'Tableau',
    bullets: ['Workbooks and dashboards rebuilt as Quick Sight dashboards', 'Calculated fields and LOD expressions converted to calculated fields', 'Extracts and Prep flows moved to AWS Glue or Redshift', 'Project permissions and user filters mapped to Quick permissions'],

    heroBanner: true,

    hero: {
      description: 'We move your Tableau workbooks, data sources, and Prep flows onto Amazon Quick and AWS, then put AI agents on top of the same data.',
      tagline: 'De-risk the move with a partner who brings hands-on migration expertise and AWS funding to the table.',
      primary: { label: 'See how we use your metadata', target: 'metadata' },
      showContact: true,
      badges: [
        { icon: 'shield', label: 'AWS-funded proofs of concept' },
        { icon: 'bolt', label: 'Built on Amazon Quick' },
      ],
    },

    components: {
      heroCard: {
        title: "Let's get started",
        description: 'We have access to AWS funding for migrations. Start with a funded assessment, then see your first dashboards live on Amazon Quick in 45 days.',
        buttonLabel: 'Start my migration',
        url: '/contact',
        icon: 'clock',
      },
      richContent: 'PlatformMigrationContent',
      platformPage: tableauPage,
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'sisense',
    title: 'Sisense to Amazon Quick',
    route: '/sisense',
    description: 'Migrate Sisense dashboards and ElastiCubes, including embedded analytics.',
    category: 'BI Migration',
    icon: 'dashboard',
    about: 'Moves Sisense content, data, and security to Amazon Quick and AWS, with AI-led testing and wave-by-wave go-lives.',
    builtWith: ['Amazon Quick Sight', 'Amazon Redshift', 'Quick Sight embedding'],

    abbr: 'Si',
    name: 'Sisense',
    bullets: ['ElastiCubes rebuilt in Amazon Redshift or SPICE', 'Widgets and dashboards recreated in Quick Sight', 'Sisense formulas converted to calculated fields', 'Embedded dashboards moved to Quick Sight embedding'],

    heroBanner: true,

    hero: {
      description: 'We move your Sisense dashboards, ElastiCubes, and embedded analytics onto Amazon Quick and AWS, then put AI agents on top of the same data.',
      tagline: 'De-risk the move with a partner who brings hands-on migration expertise and AWS funding to the table.',
      primary: { label: 'See how we use your metadata', target: 'metadata' },
      showContact: true,
      badges: [
        { icon: 'shield', label: 'AWS-funded proofs of concept' },
        { icon: 'bolt', label: 'Built on Amazon Quick' },
      ],
    },

    components: {
      heroCard: {
        title: "Let's get started",
        description: 'We have access to AWS funding for migrations. Start with a funded assessment, then see your first dashboards live on Amazon Quick in 45 days.',
        buttonLabel: 'Start my migration',
        url: '/contact',
        icon: 'clock',
      },
      richContent: 'PlatformMigrationContent',
      platformPage: sisensePage,
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'looker',
    title: 'Looker to Amazon Quick',
    route: '/looker',
    description: 'Translate LookML models and Looker dashboards into Quick datasets and dashboards.',
    category: 'BI Migration',
    icon: 'dashboard',
    about: 'Moves Looker content, data, and security to Amazon Quick and AWS, with AI-led testing and wave-by-wave go-lives.',
    builtWith: ['Amazon Quick Sight', 'Quick Sight embedding'],

    abbr: 'Lo',
    name: 'Looker',
    bullets: ['LookML views and explores mapped to Quick datasets', 'Looks and dashboards rebuilt in Quick Sight', 'Access filters mapped to row-level security', 'Embedded Looker moved to Quick Sight embedding'],

    heroBanner: true,

    hero: {
      description: 'We move your LookML models, Looks, and dashboards onto Amazon Quick and AWS, then put AI agents on top of the same data.',
      tagline: 'De-risk the move with a partner who brings hands-on migration expertise and AWS funding to the table.',
      primary: { label: 'See how we use your metadata', target: 'metadata' },
      showContact: true,
      badges: [
        { icon: 'shield', label: 'AWS-funded proofs of concept' },
        { icon: 'bolt', label: 'Built on Amazon Quick' },
      ],
    },

    components: {
      heroCard: {
        title: "Let's get started",
        description: 'We have access to AWS funding for migrations. Start with a funded assessment, then see your first dashboards live on Amazon Quick in 45 days.',
        buttonLabel: 'Start my migration',
        url: '/contact',
        icon: 'clock',
      },
      richContent: 'PlatformMigrationContent',
      platformPage: lookerPage,
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'strategy',
    title: 'Strategy (MicroStrategy) to Amazon Quick',
    route: '/strategy',
    description: 'Migrate Strategy schema objects, dossiers, and documents to Amazon Quick.',
    category: 'BI Migration',
    icon: 'dashboard',
    about: 'Moves Strategy content, data, and security to Amazon Quick and AWS, with AI-led testing and wave-by-wave go-lives.',
    builtWith: ['Amazon Quick Sight'],

    abbr: 'St',
    name: 'Strategy',
    bullets: ['Attributes, facts, and metrics mapped to Quick datasets', 'Dossiers rebuilt as interactive dashboards', 'Documents converted to paginated reports', 'Security filters mapped to row-level security'],

    heroBanner: true,

    hero: {
      description: 'We move your Strategy schema objects, dossiers, and documents onto Amazon Quick and AWS, then put AI agents on top of the same data.',
      tagline: 'De-risk the move with a partner who brings hands-on migration expertise and AWS funding to the table.',
      primary: { label: 'See how we use your metadata', target: 'metadata' },
      showContact: true,
      badges: [
        { icon: 'shield', label: 'AWS-funded proofs of concept' },
        { icon: 'bolt', label: 'Built on Amazon Quick' },
      ],
    },

    components: {
      heroCard: {
        title: "Let's get started",
        description: 'We have access to AWS funding for migrations. Start with a funded assessment, then see your first dashboards live on Amazon Quick in 45 days.',
        buttonLabel: 'Start my migration',
        url: '/contact',
        icon: 'clock',
      },
      richContent: 'PlatformMigrationContent',
      platformPage: strategyPage,
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'ssrs',
    title: 'SSRS to Amazon Quick',
    route: '/ssrs',
    description: 'Move SQL Server Reporting Services reports and subscriptions to Quick Sight paginated reports.',
    category: 'BI Migration',
    icon: 'dashboard',
    about: 'Moves SSRS content, data, and security to Amazon Quick and AWS, with AI-led testing and wave-by-wave go-lives.',
    builtWith: ['Amazon Quick Sight', 'Amazon RDS', 'Amazon Redshift'],

    abbr: 'SS',
    name: 'SSRS',
    bullets: ['RDL reports rebuilt as paginated reports', 'Report parameters converted to Quick controls', 'Subscriptions moved to scheduled email reports', 'SQL Server sources moved to Amazon RDS or Redshift'],

    heroBanner: true,

    hero: {
      description: 'We move your SSRS reports, parameters, and subscriptions onto Amazon Quick paginated reports and AWS, then put AI agents on top of the same data.',
      tagline: 'De-risk the move with a partner who brings hands-on migration expertise and AWS funding to the table.',
      primary: { label: 'See how we use your metadata', target: 'metadata' },
      showContact: true,
      badges: [
        { icon: 'shield', label: 'AWS-funded proofs of concept' },
        { icon: 'bolt', label: 'Built on Amazon Quick' },
      ],
    },

    components: {
      heroCard: {
        title: "Let's get started",
        description: 'We have access to AWS funding for migrations. Start with a funded assessment, then see your first dashboards live on Amazon Quick in 45 days.',
        buttonLabel: 'Start my migration',
        url: '/contact',
        icon: 'clock',
      },
      richContent: 'PlatformMigrationContent',
      platformPage: ssrsPage,
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'ibm-cognos',
    title: 'IBM Cognos to Amazon Quick',
    route: '/ibm-cognos',
    description: 'Migrate Cognos packages, reports, and cubes to Amazon Quick.',
    category: 'BI Migration',
    icon: 'dashboard',
    about: 'Moves IBM Cognos content, data, and security to Amazon Quick and AWS, with AI-led testing and wave-by-wave go-lives.',
    builtWith: ['Amazon Quick Sight', 'Amazon Redshift'],

    abbr: 'Co',
    name: 'IBM Cognos',
    bullets: ['Framework Manager packages mapped to Quick datasets', 'Report Studio reports converted to paginated reports', 'Cubes rebuilt in Amazon Redshift or SPICE', 'Cognos security mapped to row-level security'],

    heroBanner: true,

    hero: {
      description: 'We move your Cognos packages, reports, and cubes onto Amazon Quick and AWS, then put AI agents on top of the same data.',
      tagline: 'De-risk the move with a partner who brings hands-on migration expertise and AWS funding to the table.',
      primary: { label: 'See how we use your metadata', target: 'metadata' },
      showContact: true,
      badges: [
        { icon: 'shield', label: 'AWS-funded proofs of concept' },
        { icon: 'bolt', label: 'Built on Amazon Quick' },
      ],
    },

    components: {
      heroCard: {
        title: "Let's get started",
        description: 'We have access to AWS funding for migrations. Start with a funded assessment, then see your first dashboards live on Amazon Quick in 45 days.',
        buttonLabel: 'Start my migration',
        url: '/contact',
        icon: 'clock',
      },
      richContent: 'PlatformMigrationContent',
      platformPage: ibmCognosPage,
    },

    layout: 'VideoReportStarterLayout'
  },

  {
    id: 'spotfire',
    title: 'Spotfire to Amazon Quick',
    route: '/spotfire',
    description: 'Move Spotfire analyses and data functions to Quick Sight and AWS.',
    category: 'BI Migration',
    icon: 'dashboard',
    about: 'Moves Spotfire content, data, and security to Amazon Quick and AWS, with AI-led testing and wave-by-wave go-lives.',
    builtWith: ['Amazon Quick Sight', 'AWS Lambda', 'Amazon SageMaker'],

    abbr: 'Sp',
    name: 'Spotfire',
    bullets: ['Analysis files rebuilt as Quick Sight dashboards', 'Information links mapped to Quick datasets', 'Python and R data functions moved to AWS Lambda or SageMaker', 'Marking and filtering recreated with filters and actions'],

    heroBanner: true,

    hero: {
      description: 'We move your Spotfire analyses, information links, and data functions onto Amazon Quick and AWS, then put AI agents on top of the same data.',
      tagline: 'De-risk the move with a partner who brings hands-on migration expertise and AWS funding to the table.',
      primary: { label: 'See how we use your metadata', target: 'metadata' },
      showContact: true,
      badges: [
        { icon: 'shield', label: 'AWS-funded proofs of concept' },
        { icon: 'bolt', label: 'Built on Amazon Quick' },
      ],
    },

    components: {
      heroCard: {
        title: "Let's get started",
        description: 'We have access to AWS funding for migrations. Start with a funded assessment, then see your first dashboards live on Amazon Quick in 45 days.',
        buttonLabel: 'Start my migration',
        url: '/contact',
        icon: 'clock',
      },
      richContent: 'PlatformMigrationContent',
      platformPage: spotfirePage,
    },

    layout: 'VideoReportStarterLayout'
  },
];
